import { computed } from 'vue'
import { MemberTypes } from 'src/config/constants'
import { configurationApi } from 'src/service/configNode'
import { useAppConfigurationStore } from 'src/stores/appConfiguration.store'

export const familyMemberRequiredContactFieldsPath =
  'familyMemberRequiredContactFields'
export const familyRequiredEmergencyContactCountPath =
  'familyRequiredEmergencyContactCount'
export const defaultFamilyRequiredEmergencyContactCount = 1
export const maxFamilyRequiredEmergencyContactCount = 4

export const familyMemberContactRequirementFields = [
  'personalAddress',
  'workAddress',
  'personalPhone',
  'workPhone',
  'personalEmail',
  'workEmail',
] as const

export type FamilyMemberContactRequirementField =
  (typeof familyMemberContactRequirementFields)[number]

export type FamilyMemberContactRequirementType =
  | 'legalGuardian'
  | 'nonGuardianEmergencyContact'

export type FamilyMemberContactRequirements = Record<
  FamilyMemberContactRequirementType,
  FamilyMemberContactRequirementField[]
>

export const defaultFamilyMemberContactRequirements: FamilyMemberContactRequirements =
  {
    legalGuardian: ['personalAddress', 'personalPhone', 'personalEmail'],
    nonGuardianEmergencyContact: ['personalPhone'],
  }

export function getFamilyMemberContactRequirementType(
  memberType?: string | null,
): FamilyMemberContactRequirementType | undefined {
  if (memberType === MemberTypes.legalGuardian) return 'legalGuardian'
  if (memberType === MemberTypes.nonGuardianEmergencyContact) {
    return 'nonGuardianEmergencyContact'
  }
  return undefined
}

export function normalizeFamilyMemberContactRequirements(
  value: unknown,
): FamilyMemberContactRequirements {
  const source = isRecord(value) ? value : {}

  return {
    legalGuardian: normalizeRequirementFields(
      source.legalGuardian,
      defaultFamilyMemberContactRequirements.legalGuardian,
    ),
    nonGuardianEmergencyContact: normalizeRequirementFields(
      source.nonGuardianEmergencyContact,
      defaultFamilyMemberContactRequirements.nonGuardianEmergencyContact,
    ),
  }
}

export function useFamilyMemberContactRequirements() {
  const appConfigurationStore = useAppConfigurationStore()

  const requirements = computed(() =>
    normalizeFamilyMemberContactRequirements(
      appConfigurationStore.get(familyMemberRequiredContactFieldsPath),
    ),
  )

  const getRequirementFields = (memberType?: string | null) => {
    const requirementType = getFamilyMemberContactRequirementType(memberType)
    return requirementType ? requirements.value[requirementType] : []
  }

  const isRequired = (
    memberType: string | null | undefined,
    field: FamilyMemberContactRequirementField,
  ) => getRequirementFields(memberType).includes(field)

  return {
    requirements,
    getRequirementFields,
    isRequired,
  }
}

export function normalizeFamilyRequiredEmergencyContactCount(value: unknown) {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : defaultFamilyRequiredEmergencyContactCount

  if (!Number.isFinite(parsed)) return defaultFamilyRequiredEmergencyContactCount
  return Math.min(
    maxFamilyRequiredEmergencyContactCount,
    Math.max(defaultFamilyRequiredEmergencyContactCount, Math.floor(parsed)),
  )
}

export function useFamilyRequiredEmergencyContactCount() {
  const appConfigurationStore = useAppConfigurationStore()

  const requiredEmergencyContactCount = computed(() =>
    normalizeFamilyRequiredEmergencyContactCount(
      appConfigurationStore.get(familyRequiredEmergencyContactCountPath),
    ),
  )

  return {
    requiredEmergencyContactCount,
  }
}

export async function resolvePublicFamilyMemberContactRequirements(
  orgId?: string | null,
  centerId?: string | null,
): Promise<FamilyMemberContactRequirements> {
  if (!orgId || !centerId) {
    return defaultFamilyMemberContactRequirements
  }

  try {
    const value = await configurationApi.resolvePublicValue(
      familyMemberRequiredContactFieldsPath,
      {
        orgId,
        centerId,
      },
    )

    return normalizeFamilyMemberContactRequirements(value)
  } catch {
    return defaultFamilyMemberContactRequirements
  }
}

function normalizeRequirementFields(
  value: unknown,
  defaultValue: FamilyMemberContactRequirementField[],
) {
  if (!Array.isArray(value)) return [...defaultValue]

  const allowedFields = new Set<string>(familyMemberContactRequirementFields)
  return value.filter(
    (field): field is FamilyMemberContactRequirementField | 'email' =>
      typeof field === 'string' &&
      (allowedFields.has(field) || field === 'email'),
  ).map((field) =>
    field === 'email' ? 'personalEmail' : field,
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
